/**
 * @file    engineChartTypeRegistry.ts
 * @scope   SHARED
 * @summary Registry (Strategy) of the chart types an Enterprise widget can be rendered with.
 * @consumes Nothing — declarative data only.
 * @produces EnterpriseChartType, ENTERPRISE_CHART_TYPES, resolveEnterpriseChartType,
 *           isEnterpriseOptionValue, ENTERPRISE_OPTION_VALUES, resolveEnterpriseDescriptor,
 *           loadEngineWidget, getEnabledDescriptors, createEnterpriseChartTypeResolver,
 *           createConfigChartTypeResolver
 * @sideEffects None. Pure lookup helpers.
 * @notes Entries are full subtype descriptors (Phase 1); callers typed against the identity-only
 *        `EnterpriseChartType` keep working unchanged. The registry exists so upcoming subtypes
 *        are onboarded declaratively instead of adding `if (isDiyWidgetEngine)` branches.
 */
import { createWidgetLoader } from '../engine/serialization/loader';
import { readEnvelope } from '../engine/serialization/envelope';
import { createRenderResolver } from '../engine/render/resolveRender';
import { ENTERPRISE_DESCRIPTOR, EnterpriseDescriptor } from '../Subtypes/Enterprise/descriptor';
import { CPR_DESCRIPTOR } from '../Subtypes/CrossProgramRollup/descriptor';
import { CPR_CHART_TYPE } from '../Subtypes/CrossProgramRollup/constants';
import type { ChartTypeDescriptor } from '../engine/contracts';

export type { EnterpriseDescriptor };

/** Any registered subtype descriptor (slice/mock/action types erased). */
export type AnyEngineDescriptor = ChartTypeDescriptor<string, any, any, any>;

/** Stable identifier of an Enterprise chart type (persisted-friendly). */
export type EnterpriseChartTypeId = 'DynamicTable' | typeof CPR_CHART_TYPE;

/** Everything the drawer needs to know about one Enterprise chart type. */
export type EnterpriseChartType = {
  /** Stable discriminator, safe to persist. */
  id: EnterpriseChartTypeId;
  /**
   * Value of the chart-type dropdown option that selects this Enterprise chart type.
   * WHY: Guardrails exposes Enterprise entries as business rules, so the option `value` is the
   * rule name rather than a chart type.
   */
  optionValue: string;
  /** Widget component alias this chart type renders through. */
  component: string;
};

/**
 * Subtypes the DRAWER can build, in dropdown priority order. The first one is the default for
 * configs saved before the discriminator existed. CPR is gated by its feature flag.
 */
const DESCRIPTORS: AnyEngineDescriptor[] = [ENTERPRISE_DESCRIPTOR, CPR_DESCRIPTOR];

/**
 * Every subtype the engine knows on the READ path (render resolution). CPR is gated by its flag,
 * so with the flag off its widgets render the unsupported placeholder.
 */
const ENGINE_DESCRIPTORS: AnyEngineDescriptor[] = [ENTERPRISE_DESCRIPTOR, CPR_DESCRIPTOR];

/** Identity view of the registered descriptors (kept for existing callers). */
export const ENTERPRISE_CHART_TYPES = DESCRIPTORS as EnterpriseChartType[];

/**
 * Chart type used for Enterprise widgets saved before the discriminator existed.
 * WHY: backward compatibility — legacy configs carry no chart type and were always tables.
 */
export const DEFAULT_ENTERPRISE_CHART_TYPE = ENTERPRISE_CHART_TYPES[0];

/** Dropdown option values that select an Enterprise chart type. */
export const ENTERPRISE_OPTION_VALUES: string[] = ENTERPRISE_CHART_TYPES.map((t) => t.optionValue);

/**
 * Resolves the Enterprise chart type selected by a dropdown option value.
 *
 * @param optionValue `value` of the selected chart option.
 * @returns The matching chart type, or `undefined` when the option is a legacy chart.
 */
export const resolveEnterpriseChartType = (optionValue?: string): EnterpriseChartType | undefined =>
  ENTERPRISE_CHART_TYPES.find((type) => type.optionValue === optionValue);

/** `true` when the dropdown option value belongs to the Enterprise family. */
export const isEnterpriseOptionValue = (optionValue?: string): boolean =>
  Boolean(resolveEnterpriseChartType(optionValue));

/**
 * Resolves the full subtype descriptor (capabilities, views, state, validation, preview).
 *
 * @param optionValue `value` of the selected chart option.
 * @returns The matching descriptor, or `undefined` when the option is a legacy chart.
 */
export const resolveEnterpriseDescriptor = (
  optionValue?: string
): AnyEngineDescriptor | undefined => DESCRIPTORS.find((d) => d.optionValue === optionValue);

/**
 * Widget component an engine subtype renders through, looked up by dropdown option value.
 * WHY: engine subtypes (e.g. CPR) render through a shared component (`DynamicTable`) that the
 * widget catalogue does not map to their Guardrails chart type, so the drawer needs a fallback.
 * @param optionValue Guardrails `chartType` of the option.
 */
export const resolveEngineOptionComponent = (optionValue?: string): string | undefined =>
  resolveEnterpriseDescriptor(optionValue)?.component;

/**
 * `true` when the engine subtype selected by `optionValue` declares a trend control
 * (`hostLayout.showTrend`). Legacy charts resolve to `false`.
 * @param optionValue Guardrails `chart` / `chartType` (PascalCase, after normalization).
 */
export const engineOptionHasTrend = (optionValue?: string): boolean =>
  Boolean(resolveEnterpriseDescriptor(optionValue)?.hostLayout?.showTrend);

/**
 * Resolves a descriptor by its persisted discriminator (`chartTypeId`).
 * @param id Descriptor id (e.g. `enterpriseChartType.id`).
 */
export const resolveDescriptorById = (id?: string): AnyEngineDescriptor | undefined =>
  DESCRIPTORS.find((d) => d.id === id);

/**
 * Loads a persisted widget through the engine envelope (read-only, Phase 2).
 * Configs without `ChartTypeId`/`Chart` resolve to the default subtype (Enterprise), so today's
 * widgets load as before. The slice type depends on `chartTypeId`; callers narrow on it.
 */
export const loadEngineWidget = createWidgetLoader<unknown>(
  DESCRIPTORS,
  DEFAULT_ENTERPRISE_CHART_TYPE.id
);

/**
 * Read-path resolution (Phase 6): legacy / engine descriptor / unknown placeholder.
 * Mirrors the drawer resolver so read and write paths share the same registry and flags.
 */
export const resolveEngineRender = createRenderResolver<AnyEngineDescriptor>(
  ENGINE_DESCRIPTORS,
  DEFAULT_ENTERPRISE_CHART_TYPE.id
);

/** `(flag?) => boolean`; an undefined flag means the descriptor is not gated. */
export type FeatureFlagPredicate = (flag?: string) => boolean;

/**
 * Descriptors whose `featureFlag` is on (§12). Ungated descriptors are always kept.
 * @param isFlagEnabled Engine flag resolver (localStorage → config → default).
 */
export const getEnabledDescriptors = (isFlagEnabled: FeatureFlagPredicate): AnyEngineDescriptor[] =>
  DESCRIPTORS.filter((d) => isFlagEnabled(d.featureFlag));

/**
 * Resolves the chart type of a SAVED engine config from its envelope (`ChartTypeId` → `Chart` →
 * default). A gated-off subtype resolves to `undefined` so it never opens in the wrong builder.
 * @param isFlagEnabled Engine flag resolver.
 */
export const createConfigChartTypeResolver =
  (isFlagEnabled: FeatureFlagPredicate) =>
  (config: any): EnterpriseChartType | undefined => {
    const { chartTypeId } = readEnvelope(
      config,
      DEFAULT_ENTERPRISE_CHART_TYPE.id,
      DESCRIPTORS.map((d) => d.id)
    );
    const descriptor = DESCRIPTORS.find((d) => d.id === chartTypeId);
    return descriptor && isFlagEnabled(descriptor.featureFlag)
      ? (descriptor as EnterpriseChartType)
      : undefined;
  };

/**
 * `true` when `optionValue` belongs to a registered engine subtype whose flag is OFF.
 * WHY: a gated-off subtype must be hidden from the Chart Type dropdown, not shown as a legacy
 * option (the flag shows/hides both the option and its views).
 * @param isFlagEnabled Engine flag resolver.
 */
export const createGatedOptionPredicate =
  (isFlagEnabled: FeatureFlagPredicate) =>
  (optionValue?: string): boolean =>
    DESCRIPTORS.some((d) => d.optionValue === optionValue && !isFlagEnabled(d.featureFlag));

/**
 * Flag-aware version of `resolveEnterpriseChartType`: a gated-off descriptor resolves to
 * `undefined`, so its option behaves as a legacy chart and never routes into the engine.
 * @param isFlagEnabled Engine flag resolver.
 */
export const createEnterpriseChartTypeResolver =
  (isFlagEnabled: FeatureFlagPredicate) =>
  (optionValue?: string): EnterpriseChartType | undefined =>
    getEnabledDescriptors(isFlagEnabled).find((d) => d.optionValue === optionValue) as
      | EnterpriseChartType
      | undefined;
